'use client'
import { Skeleton } from '@heroui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { addDays, isAfter, isSameDay, startOfWeek } from 'date-fns'
import { endOfWeek } from 'date-fns'
import { useMemo } from 'react'

import { authClient } from '@/lib/auth-client'
import { useApi } from '@/trpc/react'
import { cn } from '@/utils'
interface WeekDayProps {
  day: string
  isToday?: boolean
  status?: DayStatus
}

enum DayStatus {
  Idle = 'Idle',
  Active = 'Active',
  Inactive = 'Inactive',
}

const WeekDaySkeleton = () => {
  return (
    <div className="flex flex-col items-center gap-2">
      <Skeleton className="h-[14px] w-[14px] rounded-[4px]" />
    </div>
  )
}

const colorStatusMap = {
  [DayStatus.Idle]: '#E4E4E7',
  [DayStatus.Active]: '#45C917',
  [DayStatus.Inactive]: '#F33B12',
}

const WeekDay = ({
  day,
  isToday = false,
  status = DayStatus.Idle,
}: WeekDayProps) => (
  <div className="flex flex-col items-center gap-2">
    <span
      className={cn(
        'w-[14px] text-center font-outfit text-[10px] leading-[14px]',
        isToday ? 'text-[#09090B]' : 'text-[#A1A1AA]',
      )}
    >
      {day}
    </span>
    <div
      className={cn(
        'h-[14px] w-[14px] rounded-[4px] opacity-40',
        isToday ? 'animate-pulse' : '',
      )}
      style={{ background: colorStatusMap[status] }}
    />
  </div>
)

const WeekStreak = () => {
  const api = useApi()
  const { data: session, isPending: isSessionPending } = authClient.useSession()

  const userCreatedAt = session?.user.createdAt ?? ''
  const today = useMemo(() => new Date(), [])
  const weekStart = startOfWeek(today)
  const weekEnd = endOfWeek(today)

  const { data: moments, isLoading: isLoadingMoments } = useQuery({
    ...api.moments.getByRangeDate.queryOptions({
      startDate: weekStart.toISOString(),
      endDate: weekEnd.toISOString(),
      mode: 'createdAt',
    }),
  })

  const createdAccountAt = useMemo(() => {
    if (!userCreatedAt) return null
    return new Date(userCreatedAt)
  }, [userCreatedAt])

  const daysTracked = useMemo(() => {
    // If moments are not loaded yet, return an array of idle days
    if (!moments) {
      return Array.from({ length: 7 }, (_, i) => {
        const dayDate = addDays(weekStart, i)
        const dayName = dayDate.toLocaleDateString('en-US', { weekday: 'long' })
        return [dayName, DayStatus.Idle] as [string, DayStatus]
      })
    }

    // Build a mapping of day names (e.g. "Monday") to 'true' if a moment exists on that day.
    const daysMap = moments.reduce(
      (acc, moment) => {
        const date = new Date(moment.createdAt)
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' })
        acc[dayName] = true
        return acc
      },
      {} as Record<string, boolean>,
    )

    // Build the sorted days array based on the weekStart.
    return Array.from({ length: 7 }, (_, i) => {
      const dayDate = addDays(weekStart, i)
      const dayName = dayDate.toLocaleDateString('en-US', { weekday: 'long' })
      let status: DayStatus

      // If the day is in the future or today hasn't ended yet
      if (isAfter(dayDate, today)) {
        status = DayStatus.Idle
      }
      // If day is today, mark it Active if it has moments, otherwise Idle
      else if (isSameDay(dayDate, today)) {
        status = daysMap[dayName] ? DayStatus.Active : DayStatus.Idle
      }
      // If the user wasn't created yet on that day, mark it Idle
      else if (createdAccountAt && createdAccountAt > dayDate) {
        status = DayStatus.Idle
      }
      // For past days, mark Active if a moment exists, otherwise Inactive
      else {
        status = daysMap[dayName] ? DayStatus.Active : DayStatus.Inactive
      }
      return [dayName, status] as [string, DayStatus]
    })
  }, [moments, weekStart, today, createdAccountAt])

  const isLoading = isSessionPending || isLoadingMoments

  return (
    <div className="flex gap-[6px]">
      {daysTracked.map(([day, status], i) => {
        // Determine the date corresponding to this day in the sorted list.
        const dayDate = addDays(weekStart, i)
        return isLoading ? (
          <WeekDaySkeleton key={day} />
        ) : (
          <WeekDay
            key={day}
            day={day[0]} // only the first character is shown; adjust as needed
            isToday={isSameDay(dayDate, today)}
            status={status}
          />
        )
      })}
    </div>
  )
}

export default WeekStreak
